import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { selectDeviceRequestKey } from 'src/store/apps/Common'
import { addDeviceSwitchReset } from 'src/store/apps/deviceSwitchHandlers'

/**
 * 创建设备作用域的聊天 slice 工厂：左侧摘要 + 右侧详情，切换设备时整表重置。
 *
 * @param {Object} config
 * @param {string} config.sliceName - Redux slice name
 * @param {string} config.storeKey - store 中的 state 键名
 * @param {string} config.actionPrefix - async thunk 前缀
 * @param {string} config.contactKey - 联系人对象上的唯一键字段名
 * @param {string} config.storageKey - localStorage 缓存当前选中联系人
 * @param {(args: { page: number, range?: Array }) => Promise<Object>} config.loadSummary - 拉左侧摘要
 * @param {(args: { contact: Object, page: number, range?: Array }) => Promise<Object>} config.loadDetail - 拉右侧详情
 * @param {() => Object} [config.createExtraState] - 额外初始 state
 * @param {(state: Object, payload: Object) => void} [config.applySummary] - 摘要 fulfilled 后扩展逻辑
 * @returns {{ reducer: Function, fetchSummary: Function, selectDetail: Function, removeSelectedChat: Function, setPerViewImage: Function }}
 */
export const createDeviceScopedChatSlice = ({
  sliceName,
  storeKey,
  actionPrefix,
  contactKey,
  storageKey,
  loadSummary,
  loadDetail,
  createExtraState = () => ({}),
  applySummary
}) => {
  const getContactKey = contact => contact?.[contactKey]
  const createInitialState = () => ({
    leftTotalCount: 0,
    rightTotalCount: 0,
    selectedMessage: { contact: {}, selected: [] },
    list: [],
    leftPage: 0,
    rightPage: 1,
    dateRange: null, // 当前日期筛选区间（null = 不筛选）
    summaryPending: false,
    fetchLoading: true,
    deviceRequestKey: '',
    activeSummaryRequestId: '',
    activeDetailRequestId: '',
    ...createExtraState()
  })

  const summaryRequest = createAsyncThunk(
    `${actionPrefix}/summary`,
    async ({ page, requestKey, range }) => ({
      ...(await loadSummary({ page, range })),
      page,
      requestKey
    })
  )

  const detailRequest = createAsyncThunk(
    `${actionPrefix}/detail`,
    async ({ contact, page, requestKey, range }) => ({
      ...(await loadDetail({ contact, page, range })),
      contact,
      page,
      requestKey
    })
  )

  const fetchSummary = () => (dispatch, getState) => {
    const state = getState()
    const current = state[storeKey]
    if (current.summaryPending) return Promise.resolve()

    const requestKey = selectDeviceRequestKey(state)

    return dispatch(
      summaryRequest({
        page: current.leftPage + 1,
        requestKey,
        range: current.dateRange
      })
    ).then(action => {
      const latest = getState()
      if (
        summaryRequest.fulfilled.match(action) &&
        action.payload.page === 1 &&
        action.payload.data[0] &&
        selectDeviceRequestKey(latest) === requestKey &&
        latest[storeKey].activeSummaryRequestId === action.meta.requestId
      ) {
        localStorage.setItem(storageKey, JSON.stringify(action.payload.data[0]))
      }

      return action
    })
  }

  const selectDetail = obj => (dispatch, getState) => {
    const state = getState()
    const current = state[storeKey]
    const storedContact = JSON.parse(localStorage.getItem(storageKey) || '{}')
    let contact = obj || current.selectedMessage.contact
    if (!getContactKey(contact)) contact = storedContact
    if (!getContactKey(contact)) return Promise.resolve()

    return dispatch(
      detailRequest({
        contact,
        page: obj ? 1 : current.rightPage + 1,
        requestKey: selectDeviceRequestKey(state),
        range: current.dateRange
      })
    )
  }

  const slice = createSlice({
    name: sliceName,
    initialState: createInitialState(),
    reducers: {
      removeSelectedChat: state => {
        state.selectedChat = null
      },
      setPerViewImage: (state, action) => {
        state.preViewImage = action.payload
      },
      // 设置日期筛选区间：清空已加载列表并回到第一页，由调用方随后重新 dispatch fetchSummary
      setDateRange: (state, action) => {
        state.dateRange = action.payload || null
        state.list = []
        state.leftPage = 0
        state.leftTotalCount = 0
        state.selectedMessage = { contact: {}, selected: [] }
        state.rightPage = 1
      }
    },
    extraReducers: builder => {
      addDeviceSwitchReset(builder, createInitialState)

      builder.addCase(summaryRequest.pending, (state, action) => {
        state.fetchLoading = true
        state.summaryPending = true
        state.deviceRequestKey = action.meta.arg.requestKey
        state.activeSummaryRequestId = action.meta.requestId
      })
      builder.addCase(summaryRequest.fulfilled, (state, action) => {
        if (
          state.deviceRequestKey !== action.meta.arg.requestKey ||
          state.activeSummaryRequestId !== action.meta.requestId
        ) return

        const payload = action.payload
        state.list = payload.page === 1 || payload.replaceList
          ? [...payload.data]
          : [...state.list, ...payload.data]
        state.leftPage = payload.nextLeftPage ?? payload.page
        state.leftTotalCount = payload.leftTotalCount ?? state.leftTotalCount
        if (payload.rightTotalCount != null) state.rightTotalCount = payload.rightTotalCount
        if (payload.page === 1) {
          state.selectedMessage = {
            contact: payload.data[0] || {},
            selected: payload.defaultSelectData || []
          }
          state.rightPage = 1
        }
        applySummary?.(state, payload)
        state.fetchLoading = false
        state.summaryPending = false
      })
      builder.addCase(summaryRequest.rejected, (state, action) => {
        if (
          state.deviceRequestKey === action.meta.arg.requestKey &&
          state.activeSummaryRequestId === action.meta.requestId
        ) {
          state.fetchLoading = false
          state.summaryPending = false
        }
      })

      builder.addCase(detailRequest.pending, (state, action) => {
        state.deviceRequestKey = action.meta.arg.requestKey
        state.activeDetailRequestId = action.meta.requestId
      })
      builder.addCase(detailRequest.fulfilled, (state, action) => {
        if (
          state.deviceRequestKey !== action.meta.arg.requestKey ||
          state.activeDetailRequestId !== action.meta.requestId
        ) return

        const sameContact = getContactKey(state.selectedMessage.contact) === getContactKey(action.payload.contact)
        state.selectedMessage.selected = action.payload.page > 1 && sameContact
          ? [...state.selectedMessage.selected, ...action.payload.selected]
          : action.payload.selected
        state.selectedMessage.contact = action.payload.contact
        state.rightTotalCount = action.payload.rightTotalCount
        state.rightPage = action.payload.page
      })
    }
  })

  return {
    reducer: slice.reducer,
    fetchSummary,
    selectDetail,
    removeSelectedChat: slice.actions.removeSelectedChat,
    setPerViewImage: slice.actions.setPerViewImage,
    setDateRange: slice.actions.setDateRange
  }
}
